import WorkerHealth from "./WorkerHealth";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentService } from "../../services/api";
import RecordProposals from "./RecordProposals";
import PodcastFeeds from './PodcastFeeds';
import PodcastQueue from './PodcastQueue';
import AssistantMarkdown from '../common/AssistantMarkdown';

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const field = "block text-xs font-semibold text-[#58635d]";
const kinds = [
  "podcast",
  "movie",
  "course",
  "activity",
  "gear",
  "travel",
  "gift",
  "general",
];
const blankWatch = {
  title: "",
  kind: "podcast",
  query: "",
  location: "",
  duration_minutes: 60,
  local_time: "16:00",
  weekdays: [0, 1, 2, 3, 4],
  enabled: true,
};
const date = (value, timezone) =>
  new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
const validUrl = (value) => /^https?:\/\//i.test(value || "");
const deliveryLabels = { researching: "Research in progress", ready: "Ready on time", late: "Ready late", missed: "Missed window", expired: "Finished too late", waiting: "Waiting for research", failed: "Research failed" };
function watchPayload(watch) {
  return {...Object.fromEntries(["title", "kind", "query", "location", "duration_minutes", "local_time", "weekdays", "enabled", "timing"].map((key) => [key, watch[key] ?? (key === "timing" ? null : watch[key])])), podcast_feeds:watch.podcast_feeds || []};
}

function DeliveryStatus({ delivery, timezone }) {
  if (!delivery?.status) return null;
  const clock = delivery.timezone || timezone;
  return <div className="mt-3 space-y-1 text-xs" aria-label="Discovery timing result">
    <p className="font-semibold">{deliveryLabels[delivery.status] || delivery.status}</p>
    <p>Needed by {date(delivery.needed_by, clock)} · Useful until {date(delivery.expires_at, clock)} ({clock})</p>
    {delivery.message && <p>{delivery.message}</p>}
  </div>;
}

export function CitedText({ content, citations }) {
  return <AssistantMarkdown content={content} citations={citations} />;
}

function WatchForm({ initial, busy, save, close, timezone }) {
  const [values, setValues] = useState(initial || blankWatch);
  const [useFeeds, setUseFeeds] = useState(Boolean(initial?.podcast_feeds?.length));
  const [formError, setFormError] = useState('');
  const feedMode = values.kind === 'podcast' && useFeeds;
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (feedMode && !values.podcast_feeds?.length) { setFormError('Choose a podcast feed before saving.'); return; }
        save(values.id, watchPayload({...values, podcast_feeds:feedMode ? values.podcast_feeds : [],
          query:feedMode ? 'Fresh episodes from selected podcast feeds' : values.query}));
      }}
    >
      <h3 className="font-bold">
        {initial?.id ? "Edit discovery" : "Have something useful ready for you"}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={field}>
          Name
          <input
            required
            maxLength={180}
            className={input}
            value={values.title}
            onChange={(event) => set("title", event.target.value)}
            placeholder="My ride-home listening"
          />
        </label>
        <label className={field}>
          Find
          <select
            className={input}
            value={values.kind}
            onChange={(event) => set("kind", event.target.value)}
          >
            {kinds.map((kind) => (
              <option key={kind}>{kind}</option>
            ))}
          </select>
        </label>
      </div>
      {values.kind === 'podcast' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={useFeeds} onChange={e=>setUseFeeds(e.target.checked)} />Build a listening queue from podcast feeds</label>}
      {feedMode ? <PodcastFeeds feeds={values.podcast_feeds || []} disabled={busy} onChange={feeds=>{set('podcast_feeds',feeds);setFormError('');}} /> : <label className={field}>
        What should the agent look for?
        <textarea
          required
          minLength={5}
          maxLength={2000}
          rows={3}
          className={input}
          value={values.query}
          onChange={(event) => set("query", event.target.value)}
          placeholder="Topics, interests, budget, skill level, or the decision you need help with"
        />
      </label>}
      {formError && <p role="alert" className="text-sm text-[#a14d38]">{formError}</p>}
      <p className="text-xs leading-5 text-[#68746d]">
        {feedMode ? 'Show searches go to the Apple podcast catalog. Scheduled queues use your selected public feeds and do not need AI.' : 'This request is used for live web research. Saved notes and private account records are not added to the search query.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {!feedMode && <label className={field}>
          Location (optional)
          <input
            className={input}
            value={values.location}
            onChange={(event) => set("location", event.target.value)}
          />
        </label>}
        <label className={field}>
          Target duration (minutes)
          <input
            type="number"
            min={5}
            max={600}
            className={input}
            value={values.duration_minutes ?? ""}
            onChange={(event) =>
              set(
                "duration_minutes",
                event.target.value ? Number(event.target.value) : null,
              )
            }
          />
        </label>
        <label className={field}>
          Research at ({timezone})
          <input
            required
            type="time"
            className={input}
            value={values.local_time}
            onChange={(event) => set("local_time", event.target.value)}
          />
        </label>
      </div>
      <fieldset className="space-y-3 rounded-md border border-[#dce3dd] p-3">
        <legend className="text-xs font-semibold">Prepare for a particular time</legend>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!values.timing} onChange={(e) => set("timing", e.target.checked ? { needed_by: "18:00", useful_for_minutes: 120 } : null)} />
          Set a needed-by time and usefulness window
        </label>
        {values.timing && <>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={field}>Needed by ({timezone})
              <input required type="time" className={input} value={values.timing.needed_by} onChange={(e) => set("timing", { ...values.timing, needed_by: e.target.value })} />
            </label>
            <label className={field}>Useful for after that (minutes)
              <input required type="number" min={5} max={10080} className={input} value={values.timing.useful_for_minutes} onChange={(e) => set("timing", { ...values.timing, useful_for_minutes: e.target.value === "" ? "" : Number(e.target.value) })} />
            </label>
          </div>
          <p className="text-xs text-[#68746d]">{values.timing.needed_by < values.local_time ? "The needed-by time is on the following day. " : "The needed-by time is on each selected research day. "}Late results are labeled and expire at the end of this window. Missed windows appear in Attention. Research now prepares the current useful occurrence or the next scheduled one.</p>
        </>}
      </fieldset>
      <fieldset>
        <legend className="mb-2 text-xs font-semibold">
          Days to prepare it
        </legend>
        <div className="flex flex-wrap gap-3">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
            (name, index) => (
              <label key={name} className="flex gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={values.weekdays.includes(index)}
                  onChange={(event) =>
                    set(
                      "weekdays",
                      event.target.checked
                        ? [...values.weekdays, index]
                        : values.weekdays.filter((day) => day !== index),
                    )
                  }
                />
                {name}
              </label>
            ),
          )}
        </div>
      </fieldset>
      <p className="text-xs text-[#68746d]">
        {feedMode ? 'Choose a time before your ride. Scheduled feed checks need monitoring enabled and an awake server.' : 'Choose a time before you need it. Background research uses your daily AI allowance and needs monitoring enabled.'}
      </p>
      <div className="flex gap-2">
        <button
          className="primary-button"
          disabled={busy || !values.weekdays.length}
        >
          Save discovery
        </button>
        <button className="secondary-button" type="button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function BriefCard({ brief, timezone, busy, feedback, listening }) {
  return <article className="surface-panel p-5" aria-label={`Briefing: ${brief.title}`}>
    <h3 className="text-lg font-bold">{brief.title}</h3>
    <p className="mb-4 mt-2 text-xs text-[#758078]">
      Researched {date(brief.researched_at, timezone)}{brief.expires_at && !brief.delivery?.status ? ` · Useful until ${date(brief.expires_at, timezone)}` : ''}
      {brief.expired ? " · Older information—recheck before acting" : ""}
      {brief.feedback === 'dismissed' ? ' · You dismissed this briefing' : ''}
    </p>
    <DeliveryStatus delivery={brief.delivery} timezone={timezone} />
    {!brief.podcast?.episodes?.length && <CitedText content={brief.content} citations={brief.citations} />}
    <PodcastQueue brief={brief} busy={busy} onListening={listening} />
    <div className="mt-4 flex flex-wrap gap-2">
      {[["useful", "Useful"], ["not_relevant", "Not relevant"], ["saved", "Save"], ["dismissed", "Dismiss"]].map(([value, text]) =>
        <button className={brief.feedback === value ? "primary-button text-xs" : "secondary-button text-xs"} disabled={busy} key={value} onClick={() => feedback(brief.id, value)}>{text}</button>)}
    </div>
  </article>;
}

export default function AgentDesk({
  run,
  busy,
  editProposal,
  openSettings,
  openFinance,
}) {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["agent"],
    queryFn: agentService.overview,
    refetchInterval: 15000,
  });
  const [editingWatch, setEditingWatch] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [params, setParams] = useSearchParams();
  const requestedBrief = params.get('brief');
  const selectedBrief = /^[1-9]\d{0,9}$/.test(requestedBrief || '') ? Number(requestedBrief) : null;
  const focused = useQuery({queryKey: ['agent-brief', selectedBrief], queryFn: () => agentService.brief(selectedBrief), enabled: !!selectedBrief, retry: false, refetchInterval: 15000});
  const clearBrief = () => setParams(current => {const next = new URLSearchParams(current); next.delete('brief'); return next;});
  const recordListening = (id, payload) => run(async () => {await agentService.listened(id,payload); await refetch(); if (selectedBrief===id) await focused.refetch();});
  if (isLoading) return <p role="status">Loading agent work…</p>;
  if (error)
    return (
      <section className="surface-panel p-5">
        <p role="alert">Agent activity could not load.</p>
        <button className="secondary-button mt-3" onClick={() => refetch()}>
          Try again
        </button>
      </section>
    );
  const pending = data.proposals.filter((item) => item.status === "pending");
  const briefs = data.briefs.filter(
    (item) => item.id !== selectedBrief && item.feedback !== "dismissed" && (showHistory || !item.expired),
  );
  return (
    <div className="space-y-5">
      <section className="surface-panel p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="eyebrow">Useful work while you are away</p>
            <h2 className="mt-2 text-xl font-bold">Your agent at work</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-[#68746d]">
              Turn new notes and connected information into follow-ups. Have
              researched options ready before your commute, weekend, next
              course, or trip.
            </p>
          </div>
          <button
            disabled={busy}
            className="primary-button"
            onClick={() => run(agentService.check)}
          >
            Process new information
          </button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          <span className="status-pill">
            {data.preferences.enabled ? "Monitoring on" : "Monitoring off"}
          </span>
          <span>
            {data.preferences.automatic_extraction
              ? "New sources analysed automatically"
              : "Source analysis on request"}
          </span>
          <span>
            {data.usage.used}/{data.usage.limit} AI calls today
          </span>
          {!!data.usage.reserved_research && <span>{data.usage.reserved_research} remaining calls reserved for scheduled research before background analysis</span>}
          <button className="quiet-button text-xs" onClick={openSettings}>
            Agent settings
          </button>
        </div>
      </section>

      <WorkerHealth report={data.workers} timezone={data.timezone} monitoring={data.preferences.enabled} />
      {requestedBrief && <section className="space-y-3" aria-label="Selected research briefing">
        <div className="flex items-center justify-between gap-3"><h2 className="font-bold">Your linked briefing</h2><button className="quiet-button text-sm" onClick={clearBrief}>Show all briefings</button></div>
        {focused.isLoading && <p role="status">Loading the linked briefing…</p>}
        {(!selectedBrief || focused.error) && <p role="alert" className="surface-panel p-4 text-sm">This briefing is unavailable. It may have been removed or belong to another account.</p>}
        {!focused.error && focused.data && <BriefCard brief={focused.data} timezone={data.timezone} busy={busy} listening={recordListening} feedback={(id,value) => run(async () => {await agentService.feedback(id,value); await focused.refetch();})} />}
      </section>}
      <RecordProposals run={run} busy={busy} />

      <section className="space-y-3" aria-label="Action proposals">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold">
            Responsibilities found in your information
          </h2>
          <span className="text-xs text-[#758078]">
            {pending.length} to review
          </span>
        </div>
        {!data.proposals.length && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            No suggested responsibilities waiting for review. Sync a source or
            capture a note, then run the agent.
          </p>
        )}
        {data.proposals.map((proposal) => (
          <article key={proposal.id} className="surface-panel p-5">
            <div className="flex justify-between gap-3">
              <h3 className="font-bold">{proposal.title}</h3>
              <span className="status-pill">
                {proposal.status === "automatic"
                  ? "Already tracking"
                  : proposal.task.priority}
              </span>
            </div>
            <p className="mt-2 text-xs text-[#758078]">
              {proposal.task.domain} ·{" "}
              {proposal.task.owner || "No owner specified"} ·{" "}
              {proposal.task.due_at
                ? date(proposal.task.due_at, data.timezone)
                : "Date needs clarification"}
            </p>
            <blockquote className="mt-4 border-l-2 border-[#b9d1c2] pl-3 text-sm italic text-[#58635d]">
              {proposal.evidence}
            </blockquote>
            <p className="mt-2 text-xs text-[#758078]">
              From{" "}
              {validUrl(proposal.source_url) ? (
                <a
                  className="underline"
                  href={proposal.source_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {proposal.source_title}
                </a>
              ) : (
                proposal.source_title
              )}
            </p>
            {proposal.source_url?.startsWith("https://x.com/") && <Link className="mt-2 inline-block text-xs underline" to="/social">Review X source</Link>}
            {/^mail:\d+$/.test(proposal.input_key || "") && <Link className="mt-2 inline-block text-xs underline" to={`/mail/threads/${proposal.input_key.split(":")[1]}`}>Review or refresh conversation</Link>}
            {proposal.status === "pending" && (
              <div className="mt-4 flex gap-2">
                <button
                  disabled={busy}
                  className="primary-button text-xs"
                  onClick={() =>
                    run(() =>
                      agentService.decide(proposal.id, { action: "accept" }),
                    )
                  }
                >
                  Track this
                </button>
                <button
                  className="secondary-button text-xs"
                  onClick={() => editProposal(proposal)}
                >
                  Edit first
                </button>
                <button
                  disabled={busy}
                  className="quiet-button text-xs"
                  onClick={() =>
                    run(() =>
                      agentService.decide(proposal.id, { action: "dismiss" }),
                    )
                  }
                >
                  Dismiss
                </button>
              </div>
            )}
          </article>
        ))}
      </section>

      <section className="space-y-3" aria-label="Scheduled discoveries">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold">
            Discoveries to prepare for you
          </h2>
          <button
            className="secondary-button"
            onClick={() => setEditingWatch({ ...blankWatch })}
          >
            Add discovery
          </button>
        </div>
        {editingWatch && (
          <WatchForm
            key={editingWatch.id || "new"}
            initial={editingWatch}
            timezone={data.timezone}
            busy={busy}
            close={() => setEditingWatch(null)}
            save={(id, values) =>
              run(async () => {
                if (id) await agentService.editWatch(id, values);
                else await agentService.createWatch(values);
                setEditingWatch(null);
              })
            }
          />
        )}
        {!data.watches.length && !editingWatch && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            Set a recurring request for listening, movies, courses, activities,
            gear, gift ideas, or travel research.
          </p>
        )}
        <div className="grid gap-3 xl:grid-cols-2">
          {data.watches.map((watch) => (
            <article key={watch.id} className="surface-panel p-5">
              <div className="flex justify-between gap-3">
                <h3 className="font-bold">{watch.title}</h3>
                <span className="status-pill">
                  {watch.enabled ? watch.kind : "Paused"}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#58635d]">{watch.query}</p>
              <p className="mt-3 text-xs text-[#758078]">
                Next: {date(watch.next_run_at, data.timezone)}
                {watch.location ? ` · ${watch.location}` : ""}
              </p>
              {watch.timing && <p className="mt-2 text-xs">Needed by {watch.timing.needed_by}{watch.timing.needed_by < watch.local_time ? " the following day" : " on the research day"} ({data.timezone}) · Useful for {watch.timing.useful_for_minutes} minutes after that</p>}
              <DeliveryStatus delivery={watch.delivery} timezone={data.timezone} />
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  disabled={busy || !watch.enabled}
                  className="secondary-button text-xs"
                  onClick={() => run(() => agentService.runWatch(watch.id))}
                >
                  {watch.podcast_feeds?.length ? 'Prepare listening queue' : 'Research now'}
                </button>
                {watch.routine_id ? <Link className="quiet-button text-xs" to="/assistant?view=routines">Manage routine briefing</Link> : <>
                <button
                  className="quiet-button text-xs"
                  onClick={() => setEditingWatch(watch)}
                >
                  Edit
                </button>
                <button
                  disabled={busy}
                  className="quiet-button text-xs"
                  onClick={() =>
                    run(() =>
                      agentService.editWatch(watch.id, {
                        ...watchPayload(watch),
                        enabled: !watch.enabled,
                      }),
                    )
                  }
                >
                  {watch.enabled ? "Pause" : "Resume"}
                </button>
                <button
                  disabled={busy}
                  className="quiet-button text-xs"
                  onClick={() => run(() => agentService.removeWatch(watch.id))}
                >
                  Remove
                </button>
                </>}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-3" aria-label="Researched recommendations">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold">Researched for you</h2>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={showHistory}
              onChange={(event) => setShowHistory(event.target.checked)}
            />
            Include older briefings
          </label>
        </div>
        {!briefs.length && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            Research results will appear here with clickable citations. Provider
            failures appear in activity below.
          </p>
        )}
        {briefs.map((brief) => (
          <BriefCard key={brief.id} brief={brief} timezone={data.timezone} busy={busy} listening={recordListening} feedback={(id,value) => run(() => agentService.feedback(id,value))} />
        ))}
      </section>

      <section className="surface-panel p-5">
        <h2 className="font-bold">Agent activity</h2>
        <p className="mt-2 text-xs text-[#758078]">
          Failed attempts count toward their AI or podcast feed allowance. Automatic retries wait 10
          minutes, up to three attempts per source revision.
        </p>
        {!data.runs.length && (
          <p className="mt-4 text-sm text-[#68746d]">No agent work has run yet.</p>
        )}
        {data.runs.map((job) => (
          <div className="mt-4 border-t border-[#e2e7e3] pt-4" key={job.id}>
            <div className="flex justify-between gap-3 text-xs">
              <span>
                {job.purpose === 'podcast_feed' ? 'Podcast feed check' : job.purpose === "research"
                  ? "Live research"
                  : "Source analysis"}{" "}
                · {date(job.started_at, data.timezone)}
              </span>
              <span className="status-pill">{job.status}</span>
            </div>
            {job.result?.summary && (
              <p className="mt-2 text-sm text-[#58635d]">
                {job.result.summary}
              </p>
            )}
            {job.error && (
              <p role="status" className="mt-2 text-sm text-[#a14d38]">
                {job.error}
              </p>
            )}
            {job.result?.financial_proposals > 0 && (
              <p className="mt-2 text-sm text-[#58635d]">
                {job.result.financial_proposals} financial entries identified.
                Balance changes require review in Finance.{" "}
                <button className="quiet-button text-xs" onClick={openFinance}>
                  Open financial records
                </button>
              </p>
            )}
            {job.result?.record_proposals > 0 && (
              <p className="mt-2 text-sm text-[#58635d]">
                {job.result.record_proposals} record changes prepared for review
                above.
              </p>
            )}
            {job.result?.questions?.map((question, index) => (
              <p key={index} className="mt-2 text-sm text-[#996617]">
                Needs clarification: {question}
              </p>
            ))}
          </div>
        ))}
      </section>
    </div>
  );
}
